import React, { useState, useRef, useEffect } from "react";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  MessageSquareQuote,
} from "lucide-react";
import posterImage from "../../assets/images/videoBanner.png";

interface VideoPlayerProps {
  videoUrl?: string;
  altText?: string;
  captionsUrl?: string;
  captionsLang?: string;
  initialPlaybackRate?: number;
  banner?: string;
}

// Extended video element interface to include webkit methods
interface HTMLVideoElementWithWebkit extends HTMLVideoElement {
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
  webkitDisplayingFullscreen?: boolean;
}

const VideoPlayer = ({
  videoUrl = "",
  altText = "Video player",
  captionsUrl = "",
  captionsLang = "en",
  initialPlaybackRate = 1,
  banner = posterImage,
}: VideoPlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showCaptions, setShowCaptions] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const videoRef = useRef<HTMLVideoElementWithWebkit>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hideControlsTimeout = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const container = containerRef.current;

    const handleFullscreenChange = () => {
      setIsFullscreen(
        !!(
          document.fullscreenElement ||
          (document as any).webkitFullscreenElement ||
          (document as any).mozFullScreenElement ||
          (document as any).msFullscreenElement
        ),
      );
    };

    if (container) {
      document.addEventListener("fullscreenchange", handleFullscreenChange);
      document.addEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
      document.addEventListener("mozfullscreenchange", handleFullscreenChange);
      document.addEventListener("MSFullscreenChange", handleFullscreenChange);
    }

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange,
      );
    };
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = initialPlaybackRate;
    }
  }, [initialPlaybackRate]);

  useEffect(() => {
    const video = videoRef.current;
    if (video && captionsUrl) {
      const trackElement = video.getElementsByTagName("track")[0];
      if (trackElement) {
        trackElement.addEventListener("load", () => {
          const textTrack = video.textTracks[0];
          if (textTrack) {
            textTrack.mode = showCaptions ? "showing" : "hidden";
          }
        });
      }
    }
  }, [captionsUrl, showCaptions]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleMuteUnmute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleFullscreen = () => {
    const container = containerRef.current;
    const video = videoRef.current;

    if (!container || !video) return;

    // Check if running on iOS
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

    if (isFullscreen) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      }
    } else {
      if (isIOS && video.webkitEnterFullscreen) {
        // Use webkitEnterFullscreen for iOS devices
        video.webkitEnterFullscreen();
      } else {
        // Use standard fullscreen API for other devices
        if (container.requestFullscreen) {
          container.requestFullscreen();
        } else if ((container as any).webkitRequestFullscreen) {
          (container as any).webkitRequestFullscreen();
        } else if ((container as any).mozRequestFullScreen) {
          (container as any).mozRequestFullScreen();
        } else if ((container as any).msRequestFullscreen) {
          (container as any).msRequestFullscreen();
        }
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const progress =
        (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(progress);
    }
  };

  const handleSeekChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (videoRef.current) {
      const time = (parseInt(e.target.value) / 100) * videoRef.current.duration;
      videoRef.current.currentTime = time;
      setProgress(parseInt(e.target.value));
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (hideControlsTimeout.current) {
      clearTimeout(hideControlsTimeout.current);
    }
    hideControlsTimeout.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
  };

  const toggleCaptions = () => {
    if (videoRef.current && videoRef.current.textTracks[0]) {
      const textTrack = videoRef.current.textTracks[0];
      textTrack.mode = showCaptions ? "hidden" : "showing";
      setShowCaptions(!showCaptions);
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-video max-w-5xl border border-border-gray20 rounded-[2px] mx-auto 2xl:mx-0"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => isPlaying && setShowControls(false)}
      onClick={handlePlayPause}
    >
      <video
        ref={videoRef}
        className="w-full h-full"
        src={videoUrl}
        playsInline
        poster={banner}
        onTimeUpdate={handleTimeUpdate}
        crossOrigin="anonymous"
        tabIndex={0}
        aria-labelledby="video-title"
      >
        {captionsUrl && (
          <track
            kind="captions"
            src={captionsUrl}
            srcLang={captionsLang}
            label={`Captions (${captionsLang})`}
            default
          />
        )}
      </video>

      <div
        className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 transition-opacity duration-300 ${
          showControls ? "opacity-100" : "opacity-0"
        }`}
      >
        <div className="flex items-center gap-4">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlePlayPause();
            }}
            className="text-white hover:text-gray-200"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? <Pause size={24} /> : <Play size={24} />}
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleMuteUnmute();
            }}
            className="text-white hover:text-gray-200"
            aria-label={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
          </button>

          <div className="relative flex-grow h-6 flex items-center">
            <div className="absolute w-full h-1 bg-gray-300 rounded-lg">
              <div
                className="absolute h-full bg-red-300 rounded-lg"
                style={{ width: `${progress}%` }}
              />
            </div>
            <input
              type="range"
              value={progress}
              onChange={handleSeekChange}
              className="absolute w-full h-1 appearance-none bg-transparent cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-navbar-lilly_red [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-red-500 [&::-moz-range-thumb]:border-0"
              aria-label="Seek"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {captionsUrl && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleCaptions();
              }}
              className={`text-white hover:text-gray-200 ${
                showCaptions ? "opacity-100" : "opacity-50"
              }`}
              aria-label={showCaptions ? "Hide captions" : "Show captions"}
            >
              <MessageSquareQuote size={24} />
            </button>
          )}

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleFullscreen();
            }}
            className="text-white hover:text-gray-200"
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFullscreen ? <Minimize size={24} /> : <Maximize size={24} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
